import { ANALYTICS_DEFAULTS, DEFAULT_SERIES_BUCKET } from "@/constants/workspaces";
import { workspaceSectionPath } from "@/constants/routes";
import { AUDIT_ACTION_LABELS, type AuditAction } from "@/constants/audit";
import { MetricCard } from "@/components/shared/metric-card";
import { PageHeader } from "@/components/shared/page-header";
import { EmptyState, ErrorState, LoadingState } from "@/components/shared/states";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatCurrency, formatRelative } from "@/lib/format";
import { useOrdersSeries, useRevenueSummary, useTopProducts } from "@/features/analytics/api";
import { useRecentAuditLogs } from "@/features/logs/api";
import { useWorkspace } from "@/providers/workspace-provider";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CircleDollarSign, ClipboardList, Clock, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";

/** Workspace home: revenue KPIs, order volume, top products, recent audit feed (PRD §5). */
export const WorkspaceHome = () => {
  const { workspace, settings } = useWorkspace();
  const summary = useRevenueSummary(workspace.id, ANALYTICS_DEFAULTS.rangeDays);
  const series = useOrdersSeries(workspace.id, DEFAULT_SERIES_BUCKET, ANALYTICS_DEFAULTS.rangeDays);
  const topProducts = useTopProducts(workspace.id, ANALYTICS_DEFAULTS.rangeDays);
  const logs = useRecentAuditLogs(workspace.id);

  if (summary.isLoading) return <LoadingState label="Crunching your numbers…" />;
  if (summary.isError) {
    return (
      <ErrorState
        description={summary.error instanceof Error ? summary.error.message : undefined}
        onRetry={() => void summary.refetch()}
      />
    );
  }

  const data = summary.data;
  const currency = data?.currency ?? settings.currency;

  return (
    <>
      <PageHeader
        title={workspace.name}
        description={`Last ${ANALYTICS_DEFAULTS.rangeDays} days · ${settings.timezone}`}
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          label="Settled"
          value={formatCurrency(data?.settled_amount ?? 0, currency)}
          hint="Payments received"
          tone="success"
          icon={<CircleDollarSign className="size-4" aria-hidden />}
        />
        <MetricCard
          label="Outstanding"
          value={formatCurrency(data?.outstanding_amount ?? 0, currency)}
          hint="Unpaid + DP balances"
          tone={(data?.outstanding_amount ?? 0) > 0 ? "warning" : "default"}
          icon={<Clock className="size-4" aria-hidden />}
        />
        <MetricCard
          label="Orders"
          value={String(data?.order_count ?? 0)}
          hint={`${data?.paid_order_count ?? 0} fully paid`}
          icon={<ClipboardList className="size-4" aria-hidden />}
        />
        <MetricCard
          label="Average order"
          value={formatCurrency(data?.average_order_value ?? 0, currency)}
          icon={<TrendingUp className="size-4" aria-hidden />}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Order volume</CardTitle>
          </CardHeader>
          <CardContent className="h-64">
            {(series.data?.points.length ?? 0) === 0 ? (
              <EmptyState title="No orders in range" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={series.data?.points ?? []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis
                    dataKey="bucket"
                    tickFormatter={(value: string) => value.slice(5, 10)}
                    fontSize={11}
                  />
                  <YAxis allowDecimals={false} fontSize={11} />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="order_count"
                    stroke="var(--chart-1)"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Top products</CardTitle>
          </CardHeader>
          <CardContent className="h-64">
            {(topProducts.data?.items.length ?? 0) === 0 ? (
              <EmptyState title="No product activity yet" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topProducts.data?.items ?? []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis
                    dataKey="name"
                    fontSize={11}
                    interval={0}
                    angle={-12}
                    height={48}
                    textAnchor="end"
                  />
                  <YAxis fontSize={11} />
                  <Tooltip />
                  <Bar dataKey="revenue" fill="var(--chart-2)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle>Recent activity</CardTitle>
          <Link to={workspaceSectionPath(workspace.slug, "logs")} className="text-sm text-primary">
            View all
          </Link>
        </CardHeader>
        <CardContent className="space-y-3">
          {(logs.data?.items.length ?? 0) === 0 ? (
            <EmptyState
              title="Nothing logged yet"
              description="Actions in this workspace appear here."
            />
          ) : (
            (logs.data?.items ?? []).map((log) => (
              <div key={log.id} className="flex items-center justify-between gap-3 text-sm">
                <div className="min-w-0">
                  <p className="truncate font-medium">
                    {AUDIT_ACTION_LABELS[log.action as AuditAction] ?? log.action}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">
                    {log.actor_email ?? "system"} · {log.resource}
                  </p>
                </div>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {formatRelative(log.created_at)}
                </span>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </>
  );
};
